/**
 * `/calendar?slot=<id>&date=<yyyy-mm-dd>` — the deep link that opens one
 * session's popup on the calendar page (C3, 2026-07-29).
 *
 * Both ends live here on purpose. The link is WRITTEN on the member profile's
 * Sessions card and READ by the calendar page, and the `date` half carries a
 * trap the two could quietly disagree on: it must be the session's day in the
 * STUDIO's calendar, not the viewer's browser day. A 23:30Z June session is the
 * NEXT London day — anchor the calendar on the browser's day instead and the
 * fetch window misses the very slot the link exists to show, which surfaces as
 * the honest-looking "that session isn't on the calendar any more".
 *
 * Why `date` is needed at all: the calendar's slot fetch is view-scoped (a day,
 * a week, or a 6-week month around the anchor). A session outside the current
 * view simply isn't in `slots`, so there would be nothing to select.
 */

import { isoToZonedDateKey } from "./studio-tz";

/** A `?date=` value is only honoured in this exact shape. */
export const CALENDAR_DEEP_LINK_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The `/calendar` link that opens `slotId`'s popup, anchored on the session's
 * studio day. Returns a link WITHOUT `date` when `startsAtIso` is unparseable —
 * the calendar then falls back to today, which still works whenever the session
 * is in the default view.
 */
export function buildCalendarDeepLink(
  slotId: string,
  startsAtIso: string,
): string {
  const day = isoToZonedDateKey(startsAtIso);
  const slot = `slot=${encodeURIComponent(slotId)}`;
  return day === ""
    ? `/calendar?${slot}`
    : `/calendar?${slot}&date=${encodeURIComponent(day)}`;
}

/**
 * A `?date=` param → the local-midnight day carrier the calendar anchors on,
 * or `null` for absent/malformed input (caller falls back to today).
 *
 * Deliberately NOT `new Date(key)`: that parses `YYYY-MM-DD` as UTC midnight,
 * which is the previous local day for any viewer behind UTC.
 */
export function parseCalendarDeepLinkDate(raw: string | null): Date | null {
  if (raw === null || !CALENDAR_DEEP_LINK_DATE_RE.test(raw)) return null;
  const [y, m, d] = raw.split("-").map(Number);
  if (y === undefined || m === undefined || d === undefined) return null;
  const out = new Date(y, m - 1, d);
  // Rejects real-shaped nonsense like 2026-13-40, which the Date constructor
  // would silently roll forward into a different month.
  if (
    out.getFullYear() !== y ||
    out.getMonth() !== m - 1 ||
    out.getDate() !== d
  ) {
    return null;
  }
  return out;
}
